/**
 * Cover KPI chip with calculated-metric hover context.
 */
import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import type { ReportTheme } from '../../../../types/reportTheme';
import type { CoverChip } from '../../../../lib/reportRendering/coverKpiChips';
import { explainCoverChip } from '../../../../lib/metrics/coverChipExplanations';
import { roleColor } from '../../../../lib/reportRoleColors';
import { CalculatedMetricTooltip } from '../../../metrics/CalculatedMetricTooltip';

export function CoverChipField({
  chip,
  report,
  theme,
  testIdSuffix,
  valueClassName = 'text-sm font-semibold text-text-primary tabular-nums',
  labelClassName = 'text-[10px] font-bold uppercase tracking-widest text-text-muted',
}: {
  chip: CoverChip;
  report: SiteRagReport;
  /** Resolved report theme. Optional with token-class fallback so the
   *  Hero call site (group R5) can land independently of this group. */
  theme?: ReportTheme;
  testIdSuffix?: string;
  valueClassName?: string;
  labelClassName?: string;
}): React.ReactElement {
  const suffix = testIdSuffix ?? chip.id;
  const explanation = explainCoverChip(chip, report);
  // When `theme` is provided, drive the label/value colors off the
  // resolved theme roles so the chip flips with every preset. When
  // absent (e.g. the R5 Hero call site before it threads theme), fall
  // back to the original fixed token classes for byte-stable rendering.
  const labelStyle = theme ? { color: roleColor(theme, 'muted') } : undefined;
  const valueStyle = theme ? { color: roleColor(theme, 'ink') } : undefined;
  return (
    <div className="space-y-0.5" data-testid={`cover-chip-${suffix}`}>
      <div className={`flex items-center gap-0.5 ${labelClassName}`} style={labelStyle}>
        <span>{chip.label}</span>
        <CalculatedMetricTooltip explanation={explanation} testId={`cover-chip-${suffix}-help`} />
      </div>
      <div className={valueClassName} style={valueStyle}>
        {chip.isEstimate && chip.value ? (
          <span title="Estimated value">{chip.value}</span>
        ) : (
          chip.value ?? '–'
        )}
      </div>
    </div>
  );
}
